import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { getBadgeLocation } from "../lib/menuBadgeMapping";

/**
 * Provider global maintenant le décompte des notifications NON LUES groupées
 * par emplacement UX (menu sidebar + onglets de page). Les valeurs sont
 * recalculées :
 *   - au montage (`/notifications` avec `read=false`),
 *   - à chaque réception SSE (`window` event `plania:notification`),
 *   - à chaque marquage de lecture (`plania:notifications-changed`),
 *   - via un polling de filet de sécurité (60s).
 *
 * Consommation : `useNotificationCounts()` →
 *   { byMenu: { "nav-equipements": 3 }, byPageTab: { "equipements.returns": 2 } }
 */
const NotificationCountsContext = createContext({
  byMenu: {},
  byPageTab: {},
  totalUnread: 0,
  refresh: () => {},
});

const POLL_MS = 60_000;

export function NotificationCountsProvider({ children }) {
  const { user } = useAuth();
  const [unreadItems, setUnreadItems] = useState([]);
  // Compteur temps réel des vacances actives (open, end_time non écoulé).
  // Injecté dans `byMenu["nav-historique-vacances"]` pour que le badge reste
  // visible tant qu'il existe une vacance à combler — et disparaisse dès que
  // toutes les vacances de la journée ont dépassé leur heure de fin OU ont
  // été réaffectées par un admin/manager (status → replaced).
  const [vacantActiveCount, setVacantActiveCount] = useState(0);
  const pollRef = useRef(null);
  const vacantPollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setUnreadItems([]);
      return;
    }
    try {
      // L'API renvoie l'historique complet (≤ 200 selon route) — on filtre les
      // non-lues côté client pour rester simple. Suffisant à l'échelle attendue.
      const { data } = await api.get("/notifications");
      const unread = (Array.isArray(data) ? data : []).filter((n) => !n.read);
      setUnreadItems(unread);
    } catch {
      // ignore — l'utilisateur a peut-être perdu sa session
    }
  }, [user]);

  const refreshVacantCount = useCallback(async () => {
    if (!user) {
      setVacantActiveCount(0);
      return;
    }
    // Réservé aux rôles ayant accès au menu « Interventions vacantes ».
    if (user.role !== "admin" && user.role !== "manager") {
      setVacantActiveCount(0);
      return;
    }
    try {
      const { data } = await api.get("/vacancies/active-count");
      setVacantActiveCount(Number(data?.count) || 0);
    } catch {
      // ignore — pas de session, endpoint injoignable, etc.
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(refresh, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refresh]);

  // Poll séparé pour le compteur de vacances actives — indépendant des
  // notifications, il reflète l'état "temps réel" du besoin de remplacement.
  useEffect(() => {
    refreshVacantCount();
    if (vacantPollRef.current) clearInterval(vacantPollRef.current);
    vacantPollRef.current = setInterval(refreshVacantCount, POLL_MS);
    return () => {
      if (vacantPollRef.current) clearInterval(vacantPollRef.current);
    };
  }, [refreshVacantCount]);

  useEffect(() => {
    const onNew = (e) => {
      const n = e?.detail;
      if (!n || !n.id) return;
      setUnreadItems((prev) => {
        if (prev.some((x) => x.id === n.id)) return prev;
        return [n, ...prev];
      });
    };
    const onChanged = () => refresh();
    // Événement dédié : émis après création / réaffectation / suppression
    // d'une vacance (voir HistoriqueVacances) pour rafraîchir le badge sans
    // attendre le poll.
    const onVacancyChanged = () => refreshVacantCount();
    window.addEventListener("plania:notification", onNew);
    window.addEventListener("plania:notifications-changed", onChanged);
    window.addEventListener("plania:vacancies-updated", onVacancyChanged);
    return () => {
      window.removeEventListener("plania:notification", onNew);
      window.removeEventListener("plania:notifications-changed", onChanged);
      window.removeEventListener("plania:vacancies-updated", onVacancyChanged);
    };
  }, [refresh, refreshVacantCount]);

  const value = useMemo(() => {
    const byMenu = {};
    const byPageTab = {};
    for (const n of unreadItems) {
      const loc = getBadgeLocation(n.type, user?.role);
      if (!loc) continue;
      if (loc.menuTestid) {
        byMenu[loc.menuTestid] = (byMenu[loc.menuTestid] || 0) + 1;
      }
      // Une notification peut incrémenter plusieurs onglets/sous-onglets pour
      // construire un chemin visuel complet (ex: onglet parent + sous-onglet).
      const tabs = Array.isArray(loc.pageTabs)
        ? loc.pageTabs
        : (loc.pageTab ? [loc.pageTab] : []);
      for (const key of tabs) {
        if (!key) continue;
        byPageTab[key] = (byPageTab[key] || 0) + 1;
      }
    }
    // Injection du compteur temps réel de vacances actives sur le menu dédié.
    // Il prime sur toute agrégation de notifications (source de vérité "vivante").
    if (vacantActiveCount > 0) {
      byMenu["nav-historique-vacances"] = vacantActiveCount;
    }
    return {
      byMenu,
      byPageTab,
      totalUnread: unreadItems.length,
      refresh,
    };
  }, [unreadItems, refresh, user?.role, vacantActiveCount]);

  return (
    <NotificationCountsContext.Provider value={value}>
      {children}
    </NotificationCountsContext.Provider>
  );
}

export function useNotificationCounts() {
  return useContext(NotificationCountsContext);
}
